import React from 'react';
import { ABOUT_IMAGES } from "../components/Images";
import { motion } from "framer-motion";

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-gray-50" />
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block mb-6">
              <span className="text-blue-600 text-sm font-semibold tracking-wider uppercase">About Us</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              About <span className="text-blue-600">LR Power Systems</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Learn more about our company, our mission, and our commitment to providing exceptional electrical solutions.
            </p>
            <div className="h-1 w-24 bg-blue-600 mx-auto rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div 
              className="lg:w-1/2"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src={ABOUT_IMAGES.ONE} 
                alt="LR Power Systems Team" 
                className="w-full h-auto rounded-xl shadow-xl"
              />
            </motion.div>
            <motion.div 
              className="lg:w-1/2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-lg text-gray-600 mb-6">
                Founded with a vision to revolutionize the electrical solutions industry, LR Power Systems has grown from a small startup to a trusted name in power control systems. Our journey is built on a foundation of innovation, quality, and customer satisfaction.
              </p>
              <p className="text-lg text-gray-600">
                Over the years, we've expanded our expertise and capabilities, serving diverse industries with reliable and efficient electrical solutions. Our commitment to excellence has earned us recognition as a government-approved power solutions provider.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission & Vision</h2>
              <p className="text-xl text-gray-600">
                Guiding principles that drive our commitment to excellence and innovation
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-12">
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">OUR MISSION</h3>
                <p className="text-gray-600">
                  At LR Power Systems, our mission is to empower our clients with reliable and innovative power solutions tailored to their unique needs. We are committed to delivering exceptional service and quality products that ensure uninterrupted power supply, enhance energy efficiency, and contribute to the success and sustainability of our clients' operations.
                </p>
              </motion.div>
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">OUR VISION</h3>
                <p className="text-gray-600">
                  We are recognized as a trusted leader in the power control industry, known for our expertise, reliability, and dedication to customer satisfaction. We aspire to continuously innovate and evolve our services to meet the evolving needs of our clients and contribute positively to the communities we serve. By fostering long-term partnerships and embracing sustainability, we aim to create a brighter and more resilient future for all.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Excellence",
                description: "We strive for excellence in every aspect of our work, from product quality to customer service."
              },
              {
                title: "Innovation",
                description: "We embrace innovation and continuously seek new ways to improve our solutions and services."
              },
              {
                title: "Reliability",
                description: "We are committed to providing reliable and consistent service to our clients."
              },
              {
                title: "Integrity",
                description: "We conduct our business with honesty, transparency, and ethical practices."
              },
              {
                title: "Customer Focus",
                description: "Our customers are at the heart of everything we do, and we are dedicated to their success."
              },
              {
                title: "Sustainability",
                description: "We are committed to sustainable practices that benefit our clients and the environment."
              }
            ].map((value, index) => (
              <motion.div 
                key={value.title}
                className="bg-gray-50 p-8 rounded-xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

   
    </div>
  );
};

export default AboutPage; 